import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import APIService from 'services/APIService'
import { PRIVATE_PATHS } from 'utils/paths'
import SharedBackLink from 'components/BackLink'
import { IDENTITY_FOLDER_ID, IDENTITY_LOCKED_TEXT } from 'features/bpDetails/Components/files/IdentityDocumentsFolder'

/**
 * Landing page for a "share this source" deeplink.
 *
 * Pre-flight access check: hit `/files/{file}/urls` first (cheap endpoint,
 * runs the same `FilePolicy::accessFile` gate as the insight endpoint).
 * - 200 → user has access → mount the GuruInsightModal.
 * - 403 → render a friendly "this source is restricted" card.
 * - 404 → render a "no longer exists" card.
 *
 * This avoids the modal's own error path leaking the raw JSON body
 * (`{message: "You do not have permission to view this file."}`) to the user.
 */
const GuruSourceLandingPage = () => {
  const { id: bpId, fileId } = useParams()
  const navigate = useNavigate()
  // 'checking' | 'allowed' | 'forbidden' | 'missing' | 'error'
  const [accessState, setAccessState] = useState('checking')

  useEffect(() => {
    if (!bpId || !fileId) return
    let cancelled = false
    const probe = async () => {
      try {
        const api = APIService()
        // The /urls endpoint runs the same FilePolicy::ACCESS_FILE check the
        // insight endpoint does. We don't actually need the URLs it returns
        // — we're just probing for 200 vs 403/404.
        await api.get(`/building-passport/${bpId}/files/${fileId}/urls`)
        if (!cancelled) setAccessState('allowed')
      } catch (e) {
        if (cancelled) return
        const status = e?.response?.status
        if (status === 403 && e?.response?.data?.reason === IDENTITY_FOLDER_ID) setAccessState('identity')
        else if (status === 403) setAccessState('forbidden')
        else if (status === 404) setAccessState('missing')
        else setAccessState('error')
      }
    }
    probe()
    return () => { cancelled = true }
  }, [bpId, fileId])

  const goBack = () => {
    // Land on the BP root, not /files — files can render empty for users with
    // restricted file access, leaving them stranded on a useless page. The
    // BpDetails component picks a sensible default folder per role (Guru
    // chat for full-access, Units for restricted, saved-folder otherwise).
    if (bpId) navigate(`${PRIVATE_PATHS.bpList}/${bpId}`)
    else navigate(PRIVATE_PATHS.bpList)
  }

  if (!bpId || !fileId) return null

  if (accessState === 'checking') {
    return (
      <Page>
        <Card>
          <CircularProgress size={20} sx={{ color: '#D24724' }} />
          <span>Loading source…</span>
        </Card>
      </Page>
    )
  }

  if (accessState === 'forbidden') {
    return (
      <Page>
        <BackLink onClick={goBack}>Back to Building Passport</BackLink>
        <Card $stack>
          <LockIconWrap>
            <LockOutlinedIcon style={{ fontSize: 28 }} />
          </LockIconWrap>
          <CardTitle>This source is restricted</CardTitle>
          <CardBody>
            You can see the shared Q&amp;A, but the underlying document is
            restricted to specific people on this Building Passport. Ask the
            person who shared it — or a Building Passport admin — for access.
          </CardBody>
        </Card>
      </Page>
    )
  }

  if (accessState === 'identity') {
    return (
      <Page>
        <BackLink onClick={goBack}>Back to Building Passport</BackLink>
        <Card $stack>
          <LockIconWrap>
            <LockOutlinedIcon style={{ fontSize: 28 }} />
          </LockIconWrap>
          <CardTitle>This is a personal ID document</CardTitle>
          <CardBody>{IDENTITY_LOCKED_TEXT}</CardBody>
        </Card>
      </Page>
    )
  }

  if (accessState === 'missing') {
    return (
      <Page>
        <BackLink onClick={goBack}>Back to Building Passport</BackLink>
        <Card $stack>
          <CardTitle>Source not found</CardTitle>
          <CardBody>This document no longer exists. It may have been deleted or replaced.</CardBody>
        </Card>
      </Page>
    )
  }

  if (accessState === 'error') {
    return (
      <Page>
        <BackLink onClick={goBack}>Back to Building Passport</BackLink>
        <Card $stack>
          <CardTitle>Couldn&rsquo;t load the source</CardTitle>
          <CardBody>Something went wrong. Please try again in a moment.</CardBody>
        </Card>
      </Page>
    )
  }

  // accessState === 'allowed'
  return (
    <GuruInsightModal
      open
      onClose={goBack}
      file={{ id: fileId, displayName: 'Source document' }}
      bpId={bpId}
    />
  )
}

export default GuruSourceLandingPage

const Page = styled.div`
  max-width: 540px;
  margin: 0 auto;
  padding: 48px 24px;
`

const BackLink = styled(SharedBackLink)`
  margin-bottom: 16px;
`

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: ${({ $stack }) => ($stack ? '32px 28px' : '24px')};
  text-align: ${({ $stack }) => ($stack ? 'center' : 'left')};
  display: ${({ $stack }) => ($stack ? 'block' : 'flex')};
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #475569;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const LockIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`

const CardTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
`

const CardBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #475569;
`
